import React, { useContext, useEffect, useMemo } from "react";
import Title from "../../../components/Title";
import LineGraph from "../../../components/ResponsiveLine";
import { gql, useLazyQuery } from "@apollo/client";
import AppContext, { IFilters } from "../../../AppContext";
import { Entorno } from "../../../utils/constants";
import {
  getMax,
  getMedidaSQL,
  getMin,
  getSteps,
  mergeKeyData,
} from "../../../utils/functions";
import { Spinner, SpinnerSize } from "@fluentui/react";
import Chainz from "../../../dictionaries/Chainz.json";
import { GraphNotCompatible } from "../../../components/GraphNotCompatible";
import "./style.scss";
import { useTranslation } from "react-i18next";

export const titleEn = "Trends";
export const title = `Tendencia`;
export const filters: IFilters = {
  limitedPeriodo: true,
  showMedidas: true,
  hasTicketMedio: true,
  isDesglose: false,
  showEntorno: true,
  showSegmentos: true,
  showMyData: false,
  showVariable: true,
};
const translateTopLabel = (label: string, t: (k: string) => string) => {
  if (!label) return label;
  const norm = label.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (norm === "mercado") return t("Market") as string;
  if (norm === "mi compania" || norm === "mi compañia") return t("My-company") as string;
  return t(label) !== label ? (t(label) as string) : label;
};
const Graph = () => {
  const { t } = useTranslation();
  const { semanas, filters, user } = useContext(AppContext);
  let currentFilters = React.useRef<any>(null);

  const medida = getMedidaSQL(filters.medidas);
  let entorno = filters.entorno === Entorno.Comparable ? "Comparables" : "Totales";

  let leverTypeKeys: any = ["Totales", "Canal", "Momento", "Region", "Ubicacion"];
  let leverKeys: any = {
    Canal: ["Total", "Local", "TakeAway", "Delivery"],
    Momento: [
      "Total",
      "Mañanas",
      "Tardes",
      "Comidas (L-V)",
      "Cenas (L-J)",
      "Comidas (S-D)",
      "Cenas (V-D)",
    ],
    Region: ["Total", "Z1", "Z2", "Z3", "Z4", "Z5", "Z6"],
    Ubicacion: ["Total", "Calle", "CC", "Resto"],
  };

  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $wk1: Int!
      $yr2: Int!
      $wk2: Int!
      $var: Int
      $segment: String
      $segmentType: Int
    ) {
      graph(
        Graph: "Mi tendencia"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: {
          Variables: $var
          Segmento: $segment
          SegmentType: $segmentType
        }
      )
    }
  `;

  const [getData, { loading, data, error }] = useLazyQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    getData({
      variables: {
        yr1: semanas[0].year,
        wk1: semanas[0].week,
        yr2: semanas[1].year,
        wk2: semanas[1].week,
        var: 1,
        segment:
          filters.segmentType === 0
            ? filters?.segmentos[filters?.segment]
            : filters?.especialidades[filters?.segment],
        segmentType: filters.segmentType,
      },
    });
    currentFilters.current = { ...filters };
  }, [filters.segmentType, filters.segment, semanas]);

  let errorMsg = t("An-error-has-occur-when-recovering-data");
  let isLoading = loading || data === undefined;
  let isError = !semanas || !!error;

  let labels: string[] = [],
    datalabels: string[] = [],
    dataName: string[] = [],
    values: number[][] = [],
    series: any = [],
    translatedDatalabelsForGraph: string[] = [];

  let exportData: any[] = [];
  if (!loading && !error && data) {
    try {
      const myData = data?.graph;

      if (!myData) throw new Error(errorMsg);
      if (myData?.Error) throw new Error(`${data.Error}`);

      labels = myData.map((d: any) => Object.keys(d)[0]);
      if (labels.length < 3) {
        throw new Error(t("There-is-not-enough-data-to-show-results-for-this-filter-selection") as string);
      }
      datalabels = myData[0][labels[0]].map((d: { Descripcion: string }) => d.Descripcion);
      dataName = myData[0][labels[0]].map((d: { Name: string }) => d.Name);
      const translatedDatalabelsForGraph = datalabels.map((label: string) => translateTopLabel(label, t));
      for (let i = 0; i < datalabels.length; i++) {
        if (filters.leverType === 0) {
          values.push(
            myData
              .map((dat: any) =>
                Object.values(dat).map((d: any) => d[i]?.Item[medida][entorno].Totales)
              )
              .flat()
              .reverse()
          );
        } else {
          let leverKey = leverTypeKeys[filters.leverType || 0];
          let leverSubKey = leverKeys[leverKey][filters.lever || 0];
          values.push(
            myData
              .map((dat: any) =>
                Object.values(dat).map(
                  (d: any) => d[i]?.Item[medida][entorno][leverKey][leverSubKey]
                )
              )
              .flat()
              .reverse()
          );
        }
      }

      labels.reverse();

      values.forEach((val, i) => {
        const index = Object.keys(Chainz).findIndex((item: any) => item === dataName[i]);
        let color: any = "#ffffff";
        if (index !== -1) {
          // @ts-ignore
          color = `${Chainz[dataName[i]].color}`;
        }
        series.push({
          name: translatedDatalabelsForGraph[i], 
          color: color,
          data: val,
        });
      });

      currentFilters.current = { ...filters };
      exportData = getExportData(
        myData,
        datalabels,
        labels,
        semanas[0].year === 2019,
        currentFilters.current,
        leverTypeKeys,
        leverKeys,
        t
      );
    } catch (err: any) {
      errorMsg = err.message ?? errorMsg;
    }
  }

  const max: number | undefined = useMemo(() => getMax([...values]), [values]);
  const min: number | undefined = useMemo(() => getMin([...values]), [values]);
  const stepSize = useMemo(() => getSteps(min, max), [min, max]);
  let companyColors = datalabels.includes("Mi compañía") ? ["#888888"] : [];

  const graphData = {
    datalabels: translatedDatalabelsForGraph,
    labels: labels,
    values: values,
    colors: [
      "#007070",
      ...companyColors,
      "#A30041",
      "#85b709",
      "#363aaf",
      "#9a2d79",
      "#4d947a",
      "#7EA3CF",
      "#DC9E9D",
    ],
    annotation: [],
    series: series,
    min: min,
    max: max,
    stepSize: stepSize,
  };

  return (
    <div className="chart-container">
      <br />
      <Title
        title={t("Trends")}
        error={!!error}
        files={{ data: exportData, semanas, user, title: t("Trends"), leyenda: t("Period") }}
      />
      <div className="chart">
        {!isError && (
          <LineGraph data={graphData} hue={320} annotation={"#00ff00"} percentages error={error} />
        )}
        {isError && (
          <LineGraph data={{ labels: [], datasets: [] }} hue={320} annotation={"#00ff00"} percentages error={isError} />
        )}
      </div>
      {isLoading && (
        <div className="loading-graph">
          <Spinner size={SpinnerSize.large} />
          <p>{t("Extracting-data")}</p>
        </div>
      )}
      {filters.entorno === Entorno.Comparable && filters.leverType > 0 && !loading && (
        <GraphNotCompatible>
          {t("This-graph-does-not-support-comparable-scope-for-selected-variable") ||
            "Esta gráfica no soporta el entorno comparable para la variable seleccionada."}
        </GraphNotCompatible>
      )}
      {isError && <GraphNotCompatible>{errorMsg}</GraphNotCompatible>}
    </div>
  );
};

export default Graph;




const getExportData = (
  data: any,
  datalabels: any[],
  labels: any,
  isLeap: boolean,
  currentFilters: any,
  leverTypeKeys: any,
  leverKeys: any,
  t: (k: string) => string
) => {
  const exportData: any[] = [];
  const scopes: string[] = ["Totales"];
  const measureMap: Record<string, string> = {
    Ventas: t("Sales"),
    Tickets: t("Tickets"),
    TicketMedio: t("Average-ticket"),
  };
  const scopeMap: Record<string, string> = {
    Totales: t("Totals"),
    Comparables: t("Comparables"),
  };

  const firstBlock: any = Object.values(data[0]);
  const descriptions: string[] = firstBlock[0].map((item: any) => item.Descripcion);

  if (!isLeap && currentFilters.leverType === 0) scopes.push("Comparables");


  const translatedDatalabels = datalabels.map((dl: string) => translateTopLabel(dl, t));

  for (let i = 0; i < descriptions.length; i++) {
    const tempVals: any = {};
    ["Ventas", "Tickets", "TicketMedio"].forEach((med: string) => {
      scopes.forEach((ent: string) => {
        let tempVal: any[] = [];
        if (currentFilters.leverType === 0) {
          tempVal = data
            .map((dat: any) => Object.values(dat).map((d: any) => d[i]?.Item[med][ent]))
            .flat()
            .reverse();
        } else {
          const palanca = leverTypeKeys[currentFilters.leverType];
          const subPalanca = leverKeys[palanca][currentFilters.lever];
          tempVal = data
            .map((dat: any) =>
              Object.values(dat).map((d: any) => d[i]?.Item[med][ent][palanca][subPalanca])
            )
            .flat()
            .reverse();
        }

    
        const translatedKey = `${measureMap[med]} ${scopeMap[ent]}`;
        tempVals[translatedKey] = { ...mergeKeyData(labels, tempVal) };
      });
    });

   
    const topKeyTranslated = translateTopLabel(descriptions[i], t);
    exportData.push({ [topKeyTranslated]: tempVals });
  }

 
  const allowed = new Set<string>([...datalabels, ...translatedDatalabels]);
  const exportDataFiltered = exportData.filter((item: any) => {
    const key = Object.keys(item)[0];
    return allowed.has(key);
  });

  return exportDataFiltered;
};